import * as z from 'zod';

export interface CorsiaDefault {
  totalLCAValueCorsiaDefault: number;
  coreLCAValue: number;
  inducedLandUseChange: number;
  /** [Issuance V2] made required in 03.2026 - older certificates will have this field optional */
  ghgDisplacementEmissions: number;
  /**
   * [Issuance V2] Introduced in 03.2026 - older certificates will have this fields missing
   * Should be named from CCS, same as `savingsFromCarbonAndStorage` in EU
   * */
  emissionsSavingsFromCSS: number;
}

export const ZodCorsiaDefault = z.object({
  totalLCAValueCorsiaDefault: z.number(),
  coreLCAValue: z.number(),
  inducedLandUseChange: z.number(),
  ghgDisplacementEmissions: z.number(),
  emissionsSavingsFromCSS: z.number().min(0),
});

export interface CorsiaActual {
  totalLCAValueCorsiaActual: number;
  inducedLandUseChange: number;
  production: number;
  conditioning: number;
  processing: number;
  transportation: number;
  conversionProcesses: number;
  fuelTransportation: number;
  combustion: number;
  /** [Issuance V2] made required in 03.2026 - older certificates will have this field optional */
  ghgDisplacementEmissions: number;
  emissionsSavingsDueToSoilCarbonSequestration: number;
  /** Should be named from CCS, same as `savingsFromCarbonAndStorage` in EU */
  emissionsSavingsFromCSS: number;
}

export const ZodCorsiaActual = z.object({
  totalLCAValueCorsiaActual: z.number(),
  inducedLandUseChange: z.number(),
  production: z.number(),
  conditioning: z.number(),
  processing: z.number(),
  transportation: z.number(),
  conversionProcesses: z.number(),
  fuelTransportation: z.number(),
  combustion: z.number(),
  ghgDisplacementEmissions: z.number(),
  emissionsSavingsDueToSoilCarbonSequestration: z.number().min(0),
  emissionsSavingsFromCSS: z.number().min(0),
});

export interface EuDefault {
  totalLCAValueEuDefault: number;
  inducedLandUseChange: number;
  /** [Issuance V2] made required in 03.2026 - older certificates will have this field optional */
  ghgDisplacementEmissions: number;
  /**
   * [Issuance V2] Introduced in 03.2026 - older certificates will have this fields missing
   * Same as `.emissionsSavingsFromCSS` in corsia (which should be CCS)
   */
  savingsFromCarbonAndStorage: number;
}

export const ZodEuDefault = z.object({
  totalLCAValueEuDefault: z.number(),
  inducedLandUseChange: z.number(),
  ghgDisplacementEmissions: z.number(),
  savingsFromCarbonAndStorage: z.number().min(0),
});

export interface EuActual {
  totalLCAValueEuActual: number;
  inducedLandUseChange: number;
  /**
   * [Issuance V2] made required in 03.2026 - older certificates will have this field optional
   * @NOTE: same as `ghgDisplacementEmissions`, but named differently
   */
  displacementEmissions: number;
  extractionEmissions: number;
  annualisedEmissions: number;
  processingEmissions: number;
  transportEmissions: number;
  inUseFuelEmissions: number;
  savingsFromSoil: number;
  savingsFromCarbonAndReplacement: number;
  savingsFromExcess: number;
  /** Same as `.emissionsSavingsFromCSS` in corsia (which should be CCS) */
  savingsFromCarbonAndStorage: number;
}

export const ZodEuActual = z.object({
  totalLCAValueEuActual: z.number(),
  inducedLandUseChange: z.number(),
  displacementEmissions: z.number(),
  extractionEmissions: z.number(),
  annualisedEmissions: z.number(),
  processingEmissions: z.number(),
  transportEmissions: z.number(),
  inUseFuelEmissions: z.number(),
  savingsFromSoil: z.number().min(0),
  savingsFromCarbonAndStorage: z.number().min(0),
  savingsFromCarbonAndReplacement: z.number().min(0),
  savingsFromExcess: z.number().min(0),
});

export type TotalLCAIncludeEmissions
  = | { totalLCAValueincludeEmissionsFromDeliveryToAirport: true }
    | { totalLCAValueincludeEmissionsFromDeliveryToAirport: false; useDefaultValueForDeliveryToAirport: true }
    | { totalLCAValueincludeEmissionsFromDeliveryToAirport: false; useDefaultValueForDeliveryToAirport: false; actualGHGIntensityAirportDelivery: number };

export const ZodTotalLCAIncludeEmissions = z.discriminatedUnion('totalLCAValueincludeEmissionsFromDeliveryToAirport', [
  z.object({ totalLCAValueincludeEmissionsFromDeliveryToAirport: z.literal(true) }),
  z.discriminatedUnion('useDefaultValueForDeliveryToAirport', [
    z.object({ totalLCAValueincludeEmissionsFromDeliveryToAirport: z.literal(false), useDefaultValueForDeliveryToAirport: z.literal(true) }),
    z.object({ totalLCAValueincludeEmissionsFromDeliveryToAirport: z.literal(false), useDefaultValueForDeliveryToAirport: z.literal(false), actualGHGIntensityAirportDelivery: z.number().min(0) }),
  ]),
]);
